// Shared readers for author-configurable visual options. The Options UI tracks
// a live value on `checked` / `value`, falling back to the frozen `defaultValue`
// baked into the catalog — these helpers honour that precedence so every native
// renderer (Cards, Pivot, Grid, …) reads the same effective option value.

import type { VisualOption } from './types'

/** Read the live raw value of an option (checked → value → defaultValue). */
export function readRawOption(options: VisualOption[] | undefined, name: string): string | undefined {
  for (const o of options ?? []) {
    if (!o || o.optionName !== name) continue
    const checked = (o as Record<string, unknown>).checked
    if (checked !== undefined && checked !== null) return String(checked)
    const v = (o as Record<string, unknown>).value ?? o.defaultValue
    return v == null ? undefined : String(v)
  }
  return undefined
}

/** Read a string option, falling back to `def` when unset/empty. */
export function readStrOption(options: VisualOption[] | undefined, name: string, def = ''): string {
  const v = readRawOption(options, name)
  return v == null || v === '' ? def : v
}

/** Read a numeric option, falling back to `def` when unset/non-numeric. */
export function readNumOption(options: VisualOption[] | undefined, name: string, def: number): number {
  const v = readRawOption(options, name)
  if (v == null || v === '') return def
  const n = Number(v)
  return Number.isFinite(n) ? n : def
}

/** Read a boolean option (true/on/1). When unset, returns `def` (default false). */
export function readBoolOption(options: VisualOption[] | undefined, name: string, def = false): boolean {
  const v = readRawOption(options, name)
  if (v == null) return def
  const s = v.toLowerCase()
  return s === 'true' || s === 'on' || s === '1'
}
